import React from 'react';
import { Filter, Search } from "lucide-react";
import { Combobox } from "../ui/combobox";

interface ServiceDesk {
  id: string;
  name: string;
}

interface TicketFilterBarProps {
  serviceDesks: ServiceDesk[];
  selectedDeskId: string;
  onDeskChange: (id: string) => void;
  
  statusConfig: Record<string, { label: string }>;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  
  priorityConfig: Record<string, any>;
  selectedPriority: string;
  onPriorityChange: (priority: string) => void;
  
  /** Agent names available for filtering, fed from the backend (e.g. derived from loaded tickets). */
  assignees?: string[];
  selectedAssignee?: string;
  onAssigneeChange?: (assignee: string) => void;
  
  searchQuery: string;
  onSearchChange: (query: string) => void;
  
  searchPlaceholder?: string;
  deskLabel?: string;
}

export const TicketFilterBar: React.FC<TicketFilterBarProps> = ({
  serviceDesks,
  selectedDeskId,
  onDeskChange,
  statusConfig,
  selectedStatus,
  onStatusChange,
  priorityConfig,
  selectedPriority,
  onPriorityChange,
  assignees,
  selectedAssignee = "all",
  onAssigneeChange,
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search requests...",
  deskLabel = "All Service Desks"
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 py-4 border-y">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
        <Filter className="h-4 w-4" /> Filters
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 w-full sm:w-auto">
         {/* Service Desk Filter */}
         <Combobox
            options={[
              { value: "all", label: deskLabel },
              ...serviceDesks.map((desk) => ({ value: String(desk.id), label: desk.name })),
            ]}
            value={selectedDeskId}
            onChange={(value) => onDeskChange(value ?? "all")}
            placeholder={deskLabel}
            searchPlaceholder="Search service desks..."
            className="w-full sm:w-auto sm:min-w-[150px] rounded-full shadow-sm bg-white hover:bg-gray-50"
         />

         {/* Status Filter */}
         <Combobox
            searchable={false}
            options={[
              { value: "all", label: "All Statuses" },
              ...Object.entries(statusConfig).map(([key, config]) => ({ value: key, label: config.label })),
            ]}
            value={selectedStatus}
            onChange={(value) => onStatusChange(value ?? "all")}
            placeholder="All Statuses"
            className="w-full sm:w-auto sm:min-w-[130px] rounded-full shadow-sm bg-white hover:bg-gray-50"
         />

         {/* Priority Filter */}
         <Combobox
            searchable={false}
            options={[
              { value: "all", label: "All Priorities" },
              ...Object.keys(priorityConfig).map((p) => {
                const label = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
                return { value: label, label };
              }),
            ]}
            value={selectedPriority}
            onChange={(value) => onPriorityChange(value ?? "all")}
            placeholder="All Priorities"
            className="w-full sm:w-auto sm:min-w-[130px] rounded-full shadow-sm bg-white hover:bg-gray-50"
         />

         {/* Assignee Filter */}
         {onAssigneeChange && (
            <Combobox
              options={[
                { value: "all", label: "All Assignees" },
                { value: "unassigned", label: "Unassigned Only" },
                ...(assignees || []).map((name) => ({ value: name, label: name })),
              ]}
              value={selectedAssignee}
              onChange={(value) => onAssigneeChange(value ?? "all")}
              placeholder="All Assignees"
              searchPlaceholder="Search assignees..."
              className="w-full sm:w-auto sm:min-w-[150px] rounded-full shadow-sm bg-white hover:bg-gray-50"
            />
         )}
      </div>

      <div className="sm:ml-auto relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input 
          type="text" 
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-1.5 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm"
        />
      </div>
    </div>
  );
};
